import { MoreHorizontalIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { getAggregatedValue } from '@/features/dashboard/utils/kpi-metrics';
import { KpiDialogEditor } from '@/features/projects/create-kpi/components/kpi-dialog-editor';
import { DeleteKpiDialog } from '@/features/projects/project-details/components/delete-kpi-dialog';
import { KpiAssignmentDialog } from '@/features/projects/project-details/components/kpi-assignment-dialog';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import ConnectionIcon from '@/shared/assets/connection.png';
import DeleteIcon from '@/shared/assets/delete.svg?react';
import EditIcon from '@/shared/assets/edit.svg?react';
import OneItemHighlighter from '@/shared/components/card-elements/one-item-highlighter';
import TendencyIndicator, {
  TendencyType,
} from '@/shared/components/tendency-indicator';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/shared/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import {
  getKpiUnitAbbreviationTranslation,
  getKpiUnitTranslation,
} from '@/shared/hooks/use-kpi-unit-translation';
import {
  DerivativeKpiWithMetrics,
  KpiType,
  KpiWithMetrics,
  ProjectType,
} from '@/shared/types/api';
import { formatDate, formatNumberWithDots } from '@/shared/utils/format';

const isDerivativeKpi = (
  kpi: KpiWithMetrics,
): kpi is DerivativeKpiWithMetrics => {
  return 'operation' in kpi;
};

// Types
export interface KPICardProps {
  kpi: KpiWithMetrics;
  projectType: ProjectType;
  projectId: number;
}

interface KpiDisplayData {
  headerTitle: string;
  headerSubtitle: string;
  contentTitle: string;
  contentSubtitle?: string | null;
  footerTitle: string | null;
  footerSubtitle: string | null;
  unitAbbreviation: string;
}

interface TendencyData {
  title: string;
  subtitle: string;
  tendency: TendencyType;
}

interface MenuItemProps {
  onClick: () => void;
}

const getEntrySign = (value: number): string => {
  if (value === 0) return '';
  return value > 0 ? '+' : '-';
};

const getTendencyType = (changeRate: number): TendencyType => {
  if (changeRate === 0) return 'neutral';
  return changeRate > 0 ? 'up' : 'down';
};

const hasValidTypeAndUnit = (
  type: string | null,
  unit: string | null,
): boolean => {
  return type !== null && unit !== null;
};

const hasValidUnitAndMaxValue = (
  unit: string | null,
  maxValue: number | null,
): boolean => {
  return unit !== null && maxValue !== null;
};

// Custom hooks
export const useKpiTranslations = () => {
  const { t: tKpi } = useTranslation('kpi');
  const { t: tKpiCard } = useTranslation('kpi', {
    keyPrefix: 'kpi_card',
  });
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });
  const { t: tProject } = useTranslation('project');

  return { tKpi, tKpiCard, tKpiTypes, tProject };
};

const getKpiMetrics = (kpi: KpiWithMetrics) => {
  const { metrics } = kpi;

  const previousEntryValue = metrics?.second_latest_entry_value ?? 0;
  // Only use last_entry_date from metrics - don't fall back to kpi.created_at
  // as that would show the KPI creation date instead of the last tracking entry date
  const currentEntryDate = metrics?.last_entry_date ?? null;
  const changeRate = metrics?.last_entry_ratio ?? 0;
  const currentValue = getAggregatedValue(metrics);

  return {
    previousEntryValue,
    currentEntryDate,
    changeRate,
    currentValue,
  };
};

const useKpiDisplayData = (kpi: KpiWithMetrics): KpiDisplayData => {
  const { tKpi, tKpiCard, tKpiTypes } = useKpiTranslations();
  const { previousEntryValue, currentEntryDate, currentValue } =
    getKpiMetrics(kpi);

  const { type, unit, max_value: maxValue, name } = kpi;
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(tKpiTypes, unit);
  const change = currentValue - previousEntryValue;
  const entrySign = getEntrySign(change);

  const hasTypeAndUnit = hasValidTypeAndUnit(type, unit);
  const hasUnitAndMaxValue = hasValidUnitAndMaxValue(unit, maxValue);

  return {
    headerTitle: name,
    headerSubtitle: hasTypeAndUnit
      ? tKpiCard('subtitle', {
          type: tKpi(type as KpiType),
          unit: getKpiUnitTranslation(tKpiTypes, unit),
        })
      : '',
    contentTitle: tKpiCard('content_title', {
      current_value: formatNumberWithDots(currentValue),
      unit: unitAbbreviation,
    }),
    contentSubtitle: hasUnitAndMaxValue
      ? tKpiCard('content_subtitle', {
          max_value: formatNumberWithDots(maxValue ?? 0),
          unit: unitAbbreviation,
        })
      : undefined,
    footerTitle:
      previousEntryValue === 0
        ? ''
        : tKpiCard('footer_title', {
            value: `${entrySign}${formatNumberWithDots(Math.abs(change))}`,
            unit: unitAbbreviation,
          }),
    footerSubtitle: currentEntryDate
      ? tKpiCard('footer_subtitle', {
          date: formatDate({ date: currentEntryDate, formatType: 'digits' }),
        })
      : null,
    unitAbbreviation,
  };
};

const useTendencyData = (changeRate: number): TendencyData => {
  const tendency = getTendencyType(changeRate);

  return {
    title: `${changeRate.toFixed(0)}%`,
    subtitle: 'd. letzten Eintrag',
    tendency,
  };
};

const useKpiDialogs = () => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);

  const handleDeleteClick = () => setIsDeleteDialogOpen(true);
  const handleEditClick = () => setIsEditDialogOpen(true);
  const handleAssignmentClick = () => setIsAssignmentDialogOpen(true);

  return {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isAssignmentDialogOpen,
    setIsAssignmentDialogOpen,
    handleDeleteClick,
    handleEditClick,
    handleAssignmentClick,
  };
};

// Main Component
const KPICard = ({ kpi, projectType, projectId }: KPICardProps) => {
  const kpiId = kpi.id;
  const isDerivative = isDerivativeKpi(kpi);

  const displayData = useKpiDisplayData(kpi);
  const { changeRate } = getKpiMetrics(kpi);
  const tendencyData = useTendencyData(changeRate);
  const {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isAssignmentDialogOpen,
    setIsAssignmentDialogOpen,
    handleDeleteClick,
    handleEditClick,
    handleAssignmentClick,
  } = useKpiDialogs();

  const navigate = useNavigate();

  return (
    <>
      <Card
        className="shrink-0 justify-between"
        variant="information"
        onClick={() => {
          navigate(
            paths.insights.getHref({
              kpiUniqueId: getKpiUniqueId(kpi),
              projectId: projectId.toString(),
            }),
          );
        }}
      >
        <CardHeader>
          <div className="flex flex-row items-center justify-between">
            <HeaderContent
              title={displayData.headerTitle}
              subtitle={displayData.headerSubtitle}
            />
            <div className="flex flex-row items-center justify-between gap-2">
              <TendencyHighlighter {...tendencyData} />
              <ActionsDropdown
                onDeleteClick={handleDeleteClick}
                onEditClick={handleEditClick}
                onAssignmentClick={handleAssignmentClick}
                projectType={projectType}
                kpi={kpi}
              />
            </div>
          </div>
        </CardHeader>
        <div className="h-10"></div>
        <MainContent
          contentTitle={displayData.contentTitle}
          contentSubtitle={displayData.contentSubtitle}
          footerTitle={displayData.footerTitle}
          footerSubtitle={displayData.footerSubtitle}
          isDerivative={isDerivative}
        />
      </Card>

      <DeleteKpiDialog
        kpi={kpi}
        projectId={projectId}
        name={kpi.name}
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      />

      <KpiDialogEditor
        projectKpi={kpi}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        projectType={projectType}
      />

      <KpiAssignmentDialog
        kpiId={Number(kpiId)}
        kpiName={kpi.name}
        kpiUnit={kpi.unit}
        isOpen={isAssignmentDialogOpen}
        onOpenChange={setIsAssignmentDialogOpen}
      />
    </>
  );
};

// Subcomponents
const HeaderContent = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => (
  <div className="min-w-0 flex-1">
    <CardTitle>{title}</CardTitle>
    <CardDescription>{subtitle}</CardDescription>
  </div>
);

const TendencyHighlighter = ({ title, subtitle, tendency }: TendencyData) => (
  <OneItemHighlighter
    title={title}
    subtitle={subtitle}
    icon={<TendencyIndicator tendency={tendency} />}
  />
);

const ActionsDropdown = ({
  onEditClick,
  onDeleteClick,
  onAssignmentClick,
  projectType,
  kpi,
}: {
  onEditClick: () => void;
  onDeleteClick: () => void;
  onAssignmentClick: () => void;
  projectType: ProjectType;
  kpi: KpiWithMetrics;
}) => {
  const { tKpiCard } = useKpiTranslations();
  const showAssignmentOption =
    projectType === 'project' && !isDerivativeKpi(kpi);

  // Delete nur für Nicht-Contractor-Subprojekte anzeigen
  const showDeleteOption = projectType !== 'subproject_contractor';

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant={'outline'} shape="circle" btnType="secondary">
          <MoreHorizontalIcon size={21} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent title={tKpiCard('more_options')}>
        <EditMenuItem onClick={onEditClick} />
        {showAssignmentOption && (
          <AssignmentMenuItem onClick={onAssignmentClick} />
        )}
        {showDeleteOption && <DeleteMenuItem onClick={onDeleteClick} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MainContent = ({
  contentTitle,
  contentSubtitle,
  footerTitle,
  footerSubtitle,
  isDerivative,
}: {
  contentTitle: string;
  contentSubtitle?: string | null;
  footerTitle?: string | null;
  footerSubtitle?: string | null;
  isDerivative: boolean;
}) => {
  const { tProject } = useKpiTranslations();
  const hasFooterContent = footerTitle || footerSubtitle;

  return (
    <CardContent className="flex h-full flex-col justify-between">
      <div>
        <CardTitle className="text-4xl leading-none">{contentTitle}</CardTitle>
        {contentSubtitle && (
          <CardDescription className="text-base">
            {contentSubtitle}
          </CardDescription>
        )}
      </div>
      {(hasFooterContent || isDerivative) && (
        <div className="flex items-end justify-between">
          <div>
            {isDerivative && <Badge>{tProject('KpiCard.calculated')}</Badge>}
          </div>
          {hasFooterContent && (
            <div className="text-right">
              {footerTitle && <CardTitle>{footerTitle}</CardTitle>}
              {footerSubtitle && (
                <CardDescription>{footerSubtitle}</CardDescription>
              )}
            </div>
          )}
        </div>
      )}
    </CardContent>
  );
};

const DeleteMenuItem = ({ onClick }: MenuItemProps) => {
  const { tKpiCard } = useKpiTranslations();

  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <DeleteIcon />
      {tKpiCard('actions.delete')}
    </DropdownMenuItem>
  );
};

const EditMenuItem = ({ onClick }: MenuItemProps) => {
  const { tKpiCard } = useKpiTranslations();

  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <EditIcon />
      {tKpiCard('actions.edit')}
    </DropdownMenuItem>
  );
};

const AssignmentMenuItem = ({ onClick }: MenuItemProps) => {
  const { tKpiCard } = useKpiTranslations();

  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <img src={ConnectionIcon} alt="" className="h-4 w-4" />
      {tKpiCard('actions.assign')}
    </DropdownMenuItem>
  );
};

export default KPICard;
